import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ThumbsUp, ThumbsDown, Trash2, Eye, EyeOff } from "lucide-react";
import { useRecentConversations, useTrainingExamples, useTrainingStats, useCreateTrainingExample, useUpdateTrainingExample, useDeleteTrainingExample, useToggleTrainingExampleActive } from "@/hooks/useAITraining";
import { formatDistanceToNow } from "date-fns";
import AdminTestChat from "@/components/admin/AdminTestChat";
import BotSettingsPanel from "@/components/admin/BotSettingsPanel";
import KnowledgeBasePanel from "@/components/admin/KnowledgeBasePanel";
import { ConversationQualityDashboard } from "@/components/admin/ConversationQualityDashboard";

const CATEGORIES = [
  { value: "json_leak", label: "JSON Leak" },
  { value: "tone", label: "Wrong Tone" },
  { value: "too_long", label: "Too Verbose" },
  { value: "wrong_product", label: "Wrong Product" },
  { value: "missing_context", label: "Missing Context" },
  { value: "other", label: "Other" },
];

export default function AITraining() {
  const { isAdmin, loading, user } = useAuth();
  const navigate = useNavigate();
  const [selectedConversation, setSelectedConversation] = useState<string | null>(null);
  const [feedbackModal, setFeedbackModal] = useState<{ messageId: string; userMessage: string; aiResponse: string } | null>(null);
  const [filterRating, setFilterRating] = useState<'all' | 'good' | 'bad'>('all');
  const [filterCategory, setFilterCategory] = useState<string>('all');

  useEffect(() => {
    if (!loading && !isAdmin) {
      console.log('AI Training: Redirecting non-admin user', { loading, isAdmin, user });
      navigate('/');
    }
  }, [isAdmin, loading, navigate, user]);

  if (loading) {
    console.log('AI Training: Auth loading...');
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (!isAdmin) {
    console.log('AI Training: Not admin, returning null', { isAdmin, user });
    return null;
  }

  console.log('AI Training: Rendering page for admin', { isAdmin, user });

  const { data: conversations, isLoading: loadingConversations } = useRecentConversations();
  const { data: trainingExamples, isLoading: loadingExamples } = useTrainingExamples(
    filterRating === 'all' ? {} : { rating: filterRating as 'good' | 'bad' }
  );
  const { data: stats } = useTrainingStats();

  const createExample = useCreateTrainingExample();
  const updateExample = useUpdateTrainingExample();
  const deleteExample = useDeleteTrainingExample();
  const toggleActive = useToggleTrainingExampleActive();

  const filteredExamples = trainingExamples?.filter(ex => 
    filterCategory === 'all' || ex.category === filterCategory
  );

  const handleRating = (messageId: string, userMessage: string, aiResponse: string, rating: 'good' | 'bad') => {
    if (rating === 'good') {
      createExample.mutate({
        user_message: userMessage,
        ai_response: aiResponse,
        rating: 'good',
        is_active: true,
        source_message_id: messageId,
      });
    } else {
      setFeedbackModal({ messageId, userMessage, aiResponse });
    }
  };

  const handleSubmitBadExample = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!feedbackModal) return;

    const formData = new FormData(e.currentTarget);
    const preferredResponse = formData.get('preferred_response') as string;
    const category = formData.get('category') as string;
    const notes = formData.get('notes') as string;

    createExample.mutate({
      user_message: feedbackModal.userMessage,
      ai_response: feedbackModal.aiResponse,
      preferred_response: preferredResponse,
      rating: 'bad',
      category,
      notes,
      is_active: true,
      source_message_id: feedbackModal.messageId,
    });

    setFeedbackModal(null);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-12">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Header */}
          <div>
            <h1 className="text-3xl font-bold">AI Training Center</h1>
            <p className="text-muted-foreground mt-2">
              Train your AI, manage knowledge base, and test in real-time
            </p>
          </div>

          {/* Two-Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left: Tab Content (70%) */}
            <div className="lg:col-span-2">
              <Tabs defaultValue="dashboard" className="space-y-4">
                <TabsList>
                  <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
                  <TabsTrigger value="knowledge">Knowledge Base</TabsTrigger>
                  <TabsTrigger value="settings">Bot Settings</TabsTrigger>
                  <TabsTrigger value="training">Training</TabsTrigger>
                </TabsList>

                {/* Dashboard Tab */}
                <TabsContent value="dashboard" className="space-y-4">
                  {/* Stats Cards */}
                  {stats && (
                    <div className="grid gap-4 md:grid-cols-4">
                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm font-medium">Total Examples</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="text-2xl font-bold">{stats.total}</div>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm font-medium">Good Examples</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="text-2xl font-bold text-green-600">{stats.good}</div>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm font-medium">Bad Examples</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="text-2xl font-bold text-red-600">{stats.bad}</div>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm font-medium">Active in Prompt</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="text-2xl font-bold text-blue-600">{stats.active}</div>
                        </CardContent>
                      </Card>
                    </div>
                  )}

                  {/* Quality Dashboard */}
                  <ConversationQualityDashboard />
                </TabsContent>

                {/* Knowledge Base Tab */}
                <TabsContent value="knowledge">
                  <KnowledgeBasePanel />
                </TabsContent>

                {/* Bot Settings Tab */}
                <TabsContent value="settings">
                  <BotSettingsPanel />
                </TabsContent>

                {/* Training Tab */}
                <TabsContent value="training" className="space-y-4">

                  {/* Review Conversations Section */}
            {loadingConversations ? (
              <Card>
                <CardContent className="pt-6">
                  <p className="text-muted-foreground">Loading conversations...</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {conversations?.map((conv) => (
                  <Card key={conv.id}>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="text-base">
                            {conv.conversation_summary || "Untitled Conversation"}
                          </CardTitle>
                          <CardDescription>
                            {formatDistanceToNow(new Date(conv.started_at), { addSuffix: true })}
                          </CardDescription>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSelectedConversation(selectedConversation === conv.id ? null : conv.id)}
                        >
                          {selectedConversation === conv.id ? "Hide" : "Review"}
                        </Button>
                      </div>
                    </CardHeader>

                    {selectedConversation === conv.id && (
                      <CardContent>
                        <div className="space-y-4">
                          {conv.messages.map((msg, idx) => {
                            const prevMsg = conv.messages[idx - 1];
                            const isAssistant = msg.role === "assistant";
                            const userMessage = prevMsg?.role === "user" ? prevMsg.content : "";

                            return (
                              <div
                                key={msg.id}
                                className={`p-4 rounded-lg ${
                                  isAssistant ? "bg-muted" : "bg-primary/5"
                                }`}
                              >
                                <div className="flex items-start justify-between gap-4">
                                  <div className="flex-1">
                                    <div className="font-medium text-sm mb-1">
                                      {isAssistant ? "Circle" : "User"}
                                    </div>
                                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                                  </div>
                                  {isAssistant && userMessage && (
                                    <div className="flex gap-2">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleRating(msg.id, userMessage, msg.content, 'good')}
                                      >
                                        <ThumbsUp className="h-4 w-4" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleRating(msg.id, userMessage, msg.content, 'bad')}
                                      >
                                        <ThumbsDown className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </CardContent>
                    )}
                  </Card>
                ))}
              </div>
            )}
                  
                  {/* Training Examples Section */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Training Examples</CardTitle>
                  <div className="flex gap-2">
                    <Select value={filterRating} onValueChange={(v: any) => setFilterRating(v)}>
                      <SelectTrigger className="w-[140px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Ratings</SelectItem>
                        <SelectItem value="good">Good Only</SelectItem>
                        <SelectItem value="bad">Bad Only</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={filterCategory} onValueChange={setFilterCategory}>
                      <SelectTrigger className="w-[160px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Categories</SelectItem>
                        {CATEGORIES.map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loadingExamples ? (
                  <p className="text-muted-foreground">Loading examples...</p>
                ) : (
                  <div className="space-y-4">
                    {filteredExamples?.map((example) => (
                      <div key={example.id} className="border rounded-lg p-4 space-y-3">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 space-y-2">
                            <div className="flex items-center gap-2">
                              <Badge variant={example.rating === 'good' ? 'default' : 'destructive'}>
                                {example.rating === 'good' ? <ThumbsUp className="h-3 w-3 mr-1" /> : <ThumbsDown className="h-3 w-3 mr-1" />}
                                {example.rating}
                              </Badge>
                              {example.category && (
                                <Badge variant="outline">{CATEGORIES.find(c => c.value === example.category)?.label}</Badge>
                              )}
                              <span className="text-xs text-muted-foreground">
                                {formatDistanceToNow(new Date(example.created_at), { addSuffix: true })}
                              </span>
                            </div>
                            <div>
                              <div className="text-sm font-medium mb-1">User:</div>
                              <p className="text-sm text-muted-foreground">{example.user_message}</p>
                            </div>
                            {example.ai_response && (
                              <div>
                                <div className="text-sm font-medium mb-1">AI Response:</div>
                                <p className="text-sm text-muted-foreground">{example.ai_response}</p>
                              </div>
                            )}
                            {example.preferred_response && (
                              <div>
                                <div className="text-sm font-medium mb-1 text-green-600">Preferred Response:</div>
                                <p className="text-sm">{example.preferred_response}</p>
                              </div>
                            )}
                            {example.notes && (
                              <div>
                                <div className="text-sm font-medium mb-1">Notes:</div>
                                <p className="text-sm text-muted-foreground italic">{example.notes}</p>
                              </div>
                            )}
                          </div>
                          <div className="flex flex-col gap-2">
                            <div className="flex items-center gap-2">
                              <Label htmlFor={`active-${example.id}`} className="text-xs">
                                {example.is_active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                              </Label>
                              <Switch
                                id={`active-${example.id}`}
                                checked={example.is_active}
                                onCheckedChange={(checked) => toggleActive.mutate({ id: example.id, is_active: checked })}
                              />
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteExample.mutate(example.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
                </TabsContent>
              </Tabs>
            </div>

            {/* Right: Test Chat Panel (30%) */}
            <div className="lg:col-span-1">
              <AdminTestChat />
            </div>
          </div>

        {/* Feedback Modal */}
        <Dialog open={!!feedbackModal} onOpenChange={() => setFeedbackModal(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Provide Correction</DialogTitle>
              <DialogDescription>
                Tell the AI how it should have responded
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmitBadExample} className="space-y-4">
              <div>
                <Label>User's Question</Label>
                <p className="text-sm text-muted-foreground mt-1 p-3 bg-muted rounded">
                  {feedbackModal?.userMessage}
                </p>
              </div>
              <div>
                <Label>AI's Response (Bad)</Label>
                <p className="text-sm text-muted-foreground mt-1 p-3 bg-destructive/10 rounded">
                  {feedbackModal?.aiResponse}
                </p>
              </div>
              <div>
                <Label htmlFor="preferred_response">How Should It Have Responded? *</Label>
                <Textarea
                  id="preferred_response"
                  name="preferred_response"
                  required
                  rows={4}
                  className="mt-1"
                  placeholder="Write the response you wanted..."
                />
              </div>
              <div>
                <Label htmlFor="category">Category *</Label>
                <Select name="category" required>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select issue type" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="notes">Notes (Optional)</Label>
                <Input
                  id="notes"
                  name="notes"
                  className="mt-1"
                  placeholder="Additional context..."
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setFeedbackModal(null)}>
                  Cancel
                </Button>
                <Button type="submit">Save Training Example</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </main>
      <Footer />
    </div>
  );
}
